import type { IntakeAnswers, TherapyType } from './types';
import {
  getActiveTherapyTypes,
  getBookedProviderIds,
  getTherapyTypesForSubmission,
  hasSelectedGroupCohort,
  intakeBooksNothing,
  isHighRisk,
} from './types';
import { intakeDemographicsSchema } from '@/schemas/intake-demographics.schema';

const US_ZIP_REGEX = /^\d{5}$/;

export interface StepValidationContext {
  billingPatientId?: string;
  intakeLeadPatientId?: string | null;
  profileResourceType?: string;
  currentRoundTherapyType?: TherapyType;
}

export function isStepValid(
  step: number,
  answers: IntakeAnswers,
  _ctx: StepValidationContext = {},
): boolean {
  switch (step) {
    case 0:
      return answers.therapyTypes.length > 0;
    case 14:
      return answers.groupSelection === 'help-find' || hasSelectedGroupCohort(answers);
    case 1:
      return answers.topics.length > 0;
    case 2:
      return true;
    case 3:
      return (
        answers.screeningEmergency != null &&
        answers.screeningHospitalization != null &&
        answers.screeningSuicidalIdeation != null &&
        answers.screeningMedications != null &&
        answers.screeningSubstanceUse != null &&
        answers.screeningLegalProceedings != null
      );
    case 4:
      return answers.format != null;
    case 5:
      return intakeDemographicsSchema.safeParse({
        firstName: answers.firstName,
        lastName: answers.lastName,
        email: answers.email,
        dateOfBirth: answers.dateOfBirth,
        zip: answers.zip,
      }).success;
    case 6:
      if (answers.existingClient) return false;
      return /^\d{6}$/.test(answers.emailVerificationCode);
    case 7:
      return answers.preferredLocationIds.length > 0;
    case 8:
      if (answers.payType == null) return false;
      if (answers.payType === 'insurance') {
        if (
          answers.insurancePayerOrgId == null ||
          answers.insurancePayerOrgId === '' ||
          answers.memberId.trim() === ''
        ) {
          return false;
        }
        // Group ID is optional (FHIR/PRD: "if available"). When the subscriber is
        // not the patient, subscriber demographics are required.
        if (answers.subscriberRelationship !== 'self') {
          return (
            answers.subscriberFirstName.trim() !== '' &&
            answers.subscriberLastName.trim() !== '' &&
            answers.subscriberDateOfBirth.trim() !== ''
          );
        }
        return true;
      }
      return true;
    case 9:
      return getActiveTherapyTypes(answers).length >= 1;
    case 10: {
      const currentType = _ctx.currentRoundTherapyType;
      if (currentType && (answers.waitlistedTherapyTypes ?? []).includes(currentType)) return true;
      if (answers.selectedProviderId == null) return false;
      return !getBookedProviderIds(answers).has(answers.selectedProviderId);
    }
    case 11:
      return answers.sessionType != null;
    case 12: {
      const hasDateTime =
        answers.bookingDate != null && answers.bookingTime != null && answers.bookingTime.trim() !== '';
      if (!hasDateTime) return false;
      // High-risk screening booking also requires a resolved team slot (start/end/practitioner),
      // so a stale bookingDate/bookingTime alone can't satisfy validation.
      if (isHighRisk(answers)) {
        return (
          answers.screeningSlotStart != null &&
          answers.screeningSlotEnd != null &&
          answers.screeningPractitionerId != null
        );
      }
      return true;
    }
    case 13:
      if (answers.format == null) return false;
      // Group-only submits as a pending request with no appointment, so requiring a booking here
      // would make it impossible to finish. Everything else still needs one.
      if (intakeBooksNothing(answers)) {
        return getTherapyTypesForSubmission(answers).length > 0;
      }
      return answers.bookings.some((b) => !b.cancelled);
    default:
      return false;
  }
}

export function validateZip(zip: string): boolean {
  return US_ZIP_REGEX.test(zip.trim());
}
